// src/components/Nutrition.js
import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  IoRestaurantOutline,
  IoNutritionOutline,
  IoChevronDown,
  IoChevronUp,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoCalendarOutline,
  IoFlaskOutline,
  IoTimeOutline,
} from 'react-icons/io5';

const GOAL_LABELS = {
  bulk: 'Volumen',
  cut: 'Definición',
  maintain: 'Mantenimiento',
  recomp: 'Recomposición',
};

const MacroBadge = ({ label, value, color }) =>
  value != null ? (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {value.toFixed(1)}{label === 'kcal' ? '' : 'g'} {label}
    </span>
  ) : null;

const MealCard = ({ meal }) => {
  const [open, setOpen] = useState(false);
  const totals = meal.items.reduce(
    (acc, item) => ({
      calories: acc.calories + (item.calories || 0),
      protein_g: acc.protein_g + (item.protein_g || 0),
      carbs_g: acc.carbs_g + (item.carbs_g || 0),
      fat_g: acc.fat_g + (item.fat_g || 0),
    }),
    { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }
  );
  const hasMacros = meal.items.some((i) => i.calories != null);

  return (
    <div className="glass-panel rounded-xl overflow-hidden animate-fade-up">
      <button
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          <IoRestaurantOutline size={20} className="text-marizan-red flex-shrink-0" />
          <div>
            <h3 className="font-bold text-white">{meal.name}</h3>
            {meal.notes && <p className="text-gray-400 text-xs mt-0.5">{meal.notes}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasMacros && (
            <div className="hidden sm:flex gap-2">
              <MacroBadge label="kcal" value={totals.calories} color="bg-orange-900 text-orange-300" />
              <MacroBadge label="P" value={totals.protein_g} color="bg-blue-900 text-blue-300" />
              <MacroBadge label="C" value={totals.carbs_g} color="bg-yellow-900 text-yellow-300" />
              <MacroBadge label="G" value={totals.fat_g} color="bg-green-900 text-green-300" />
            </div>
          )}
          {open ? <IoChevronUp size={18} className="text-gray-400" /> : <IoChevronDown size={18} className="text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-700/70 p-4">
          {meal.items.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin alimentos registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-xs border-b border-gray-700">
                    <th className="text-left pb-2 pr-3">Alimento</th>
                    <th className="text-right pb-2 px-2">Cant.</th>
                    <th className="text-left pb-2 px-2">Unid.</th>
                    <th className="text-right pb-2 px-2">kcal</th>
                    <th className="text-right pb-2 px-2">Prot.</th>
                    <th className="text-right pb-2 px-2">Carbs</th>
                    <th className="text-right pb-2 pl-2">Grasas</th>
                  </tr>
                </thead>
                <tbody>
                  {meal.items.map((item) => (
                    <tr key={item.id} className="border-b border-gray-800 last:border-0">
                      <td className="py-2 pr-3 font-medium text-white">{item.food_name}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{item.quantity ?? '—'}</td>
                      <td className="py-2 px-2 text-gray-400">{item.unit ?? ''}</td>
                      <td className="py-2 px-2 text-right text-orange-300">{item.calories != null ? item.calories.toFixed(0) : '—'}</td>
                      <td className="py-2 px-2 text-right text-blue-300">{item.protein_g != null ? `${item.protein_g.toFixed(1)}g` : '—'}</td>
                      <td className="py-2 px-2 text-right text-yellow-300">{item.carbs_g != null ? `${item.carbs_g.toFixed(1)}g` : '—'}</td>
                      <td className="py-2 pl-2 text-right text-green-300">{item.fat_g != null ? `${item.fat_g.toFixed(1)}g` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                {hasMacros && (
                  <tfoot>
                    <tr className="text-xs font-bold border-t border-gray-600">
                      <td colSpan={3} className="pt-2 text-gray-400">Total comida</td>
                      <td className="pt-2 text-right text-orange-300">{totals.calories.toFixed(0)}</td>
                      <td className="pt-2 text-right text-blue-300">{totals.protein_g.toFixed(1)}g</td>
                      <td className="pt-2 text-right text-yellow-300">{totals.carbs_g.toFixed(1)}g</td>
                      <td className="pt-2 text-right text-green-300">{totals.fat_g.toFixed(1)}g</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Nutrition = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDayIdx, setSelectedDayIdx] = useState(null); // null = today

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await api.get('/me/nutrition-plan');
        setData(res.data);
      } catch (err) {
        setError('No se pudo cargar tu plan de nutrición.');
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, []);

  if (loading) {
    return (
      <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
        <div className="skeleton h-10 w-56 rounded mb-3" />
        <div className="skeleton h-4 w-80 rounded mb-8" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[1, 2, 3, 4].map((item) => <div key={item} className="skeleton h-20 rounded-xl" />)}
        </div>
        <div className="space-y-3">
          {[1, 2].map((item) => <div key={item} className="skeleton h-24 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (error) return <p className="text-red-400 text-center py-10">{error}</p>;

  if (!data) {
    return (
      <div className="w-full container mx-auto p-4 md:p-8 text-white">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Nutrición</h1>
        <div className="text-center border-2 border-dashed border-gray-600 rounded-xl p-12 mt-8">
          <IoNutritionOutline size={48} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 text-lg">Tu coach aún no te ha asignado un plan de nutrición.</p>
        </div>
      </div>
    );
  }

  const { plan, today_day_plan } = data;
  const sortedDays = [...(plan.daily_plans || [])].sort((a, b) => a.day_number - b.day_number);

  const todayDayId = today_day_plan?.id ?? null;
  const todayIdx = sortedDays.findIndex((d) => d.id === todayDayId);
  const effectiveIdx = selectedDayIdx !== null ? selectedDayIdx : (todayIdx >= 0 ? todayIdx : 0);
  const activeDayPlan = sortedDays[effectiveIdx] ?? today_day_plan;

  const dayTotals = activeDayPlan?.meals?.reduce(
    (acc, meal) => {
      meal.items.forEach((item) => {
        acc.calories += item.calories || 0;
        acc.protein_g += item.protein_g || 0;
        acc.carbs_g += item.carbs_g || 0;
        acc.fat_g += item.fat_g || 0;
      });
      return acc;
    },
    { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }
  ) ?? { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };

  const hasDayMacros = activeDayPlan?.meals?.some((m) => m.items.some((i) => i.calories != null));
  const isToday = effectiveIdx === todayIdx;

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl md:text-4xl font-bold">Nutrición</h1>
          {plan.goal && (
            <span className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full">
              {GOAL_LABELS[plan.goal] || plan.goal}
            </span>
          )}
        </div>
        <p className="text-gray-400 text-sm">{plan.name}</p>
        {plan.description && <p className="text-gray-500 text-xs mt-1">{plan.description}</p>}
      </div>

      {sortedDays.length > 1 && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <IoCalendarOutline size={16} className="text-gray-400" />
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Selecciona el día</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedDayIdx(Math.max(0, effectiveIdx - 1))}
              disabled={effectiveIdx === 0}
              className="p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <IoChevronBackOutline size={18} />
            </button>
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-2 min-w-max">
                {sortedDays.map((day, idx) => {
                  const isActive = idx === effectiveIdx;
                  const isTodayDay = idx === todayIdx;
                  return (
                    <button
                      key={day.id}
                      onClick={() => setSelectedDayIdx(idx)}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
                        isActive
                          ? 'bg-marizan-red border-marizan-red text-white'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/30'
                      }`}
                    >
                      {day.name || `Día ${day.day_number}`}
                      {isTodayDay && <span className="ml-1 text-yellow-400">●</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              onClick={() => setSelectedDayIdx(Math.min(sortedDays.length - 1, effectiveIdx + 1))}
              disabled={effectiveIdx === sortedDays.length - 1}
              className="p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <IoChevronForwardOutline size={18} />
            </button>
          </div>
        </div>
      )}

      {hasDayMacros && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Calorías', value: `${dayTotals.calories.toFixed(0)} kcal`, color: 'text-orange-300' },
            { label: 'Proteínas', value: `${dayTotals.protein_g.toFixed(1)} g`, color: 'text-blue-300' },
            { label: 'Carbohidratos', value: `${dayTotals.carbs_g.toFixed(1)} g`, color: 'text-yellow-300' },
            { label: 'Grasas', value: `${dayTotals.fat_g.toFixed(1)} g`, color: 'text-green-300' },
          ].map(({ label, value, color }) => (
            <div key={label} className="glass-panel rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase tracking-wide">{label}</p>
              <p className={`text-xl font-bold mt-1 ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {activeDayPlan ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">
                {activeDayPlan.name || `Día ${activeDayPlan.day_number}`}
                <span className="text-gray-500 text-sm font-normal ml-2">
                  — {activeDayPlan.meals?.length ?? 0} comidas
                </span>
              </h2>
              {isToday && (
                <p className="text-xs text-yellow-500 mt-0.5">
                  Hoy — {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-3">
            {activeDayPlan.meals?.map((meal) => (
              <MealCard key={meal.id} meal={meal} />
            ))}
            {(!activeDayPlan.meals || activeDayPlan.meals.length === 0) && (
              <p className="text-gray-400 text-center py-8">Este día no tiene comidas registradas.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center border-2 border-dashed border-gray-600 rounded-xl p-10">
          <p className="text-gray-400">El plan asignado no tiene días definidos.</p>
        </div>
      )}

      {plan.supplements && plan.supplements.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <IoFlaskOutline size={20} className="text-marizan-red" />
            <h2 className="text-xl font-semibold">Suplementos</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {plan.supplements.map((s) => (
              <div key={s.id} className="glass-panel rounded-xl p-4">
                <p className="font-bold text-white">{s.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  {s.dose && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-marizan-red/20 text-marizan-red border border-marizan-red/30">
                      {s.dose}
                    </span>
                  )}
                  {s.timing && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <IoTimeOutline size={12} /> {s.timing}
                    </span>
                  )}
                </div>
                {s.notes && <p className="text-xs text-gray-500 mt-1.5">{s.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Nutrition;
